import { createContext, useContext, useState, useEffect, type ReactNode } from "react";

export interface CartItem {
  productId: string;
  slug: string;
  name: string;
  imageUrl: string;
  unitPrice: number;
  quantity: number;
  variant?: string;
  variantPriceAdjustment?: number;
}

interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (productId: string, variant?: string) => void;
  updateQuantity: (productId: string, quantity: number, variant?: string) => void;
  clearCart: () => void;
  itemCount: number;
  subtotal: number;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_KEY = "florals-cart-v2";

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(loadCart);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  }, [items]);

  const itemKey = (id: string, variant?: string) => `${id}__${variant || ""}`;

  const addItem = (item: CartItem) => {
    setItems((prev) => {
      const key = itemKey(item.productId, item.variant);
      const existing = prev.find((i) => itemKey(i.productId, i.variant) === key);
      if (existing) {
        return prev.map((i) =>
          itemKey(i.productId, i.variant) === key
            ? { ...i, quantity: i.quantity + item.quantity }
            : i
        );
      }
      return [...prev, item];
    });
    setIsOpen(true);
  };

  const removeItem = (productId: string, variant?: string) => {
    const key = itemKey(productId, variant);
    setItems((prev) => prev.filter((i) => itemKey(i.productId, i.variant) !== key));
  };

  const updateQuantity = (productId: string, quantity: number, variant?: string) => {
    if (quantity <= 0) return removeItem(productId, variant);
    const key = itemKey(productId, variant);
    setItems((prev) =>
      prev.map((i) =>
        itemKey(i.productId, i.variant) === key ? { ...i, quantity } : i
      )
    );
  };

  const clearCart = () => setItems([]);

  const itemCount = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce(
    (s, i) => s + (i.unitPrice + (i.variantPriceAdjustment || 0)) * i.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQuantity, clearCart, itemCount, subtotal, isOpen, setIsOpen }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
